import type {
  TeamChatJoinParams,
  TeamChatJoinResponse,
} from "@bezoned/shared/types/api/team/team-chat-join.types.js";
import { useCallback, useEffect, useRef, useState } from "react";

import { teamChatJoin } from "../../services/api.service.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";

type RefreshTokenFn = () => Promise<{ token: string; expiresOn: Date }>;

// 23 hours — ACS tokens are valid for 24 h; refresh before expiry
const TOKEN_EXPIRY_MS = 82_800_000;

interface UseChatChannelOptions {
  params: TeamChatJoinParams | null;
}

interface UseChatChannelResult {
  credentials: TeamChatJoinResponse | null;
  threadId: string | null;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
  refreshToken: RefreshTokenFn | null;
}

export function useChatChannel(
  options: UseChatChannelOptions
): UseChatChannelResult {
  const { params } = options;
  const [credentials, setCredentials] = useState<TeamChatJoinResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const requestIdRef = useRef(0);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  // Lint exception: retryCount is an intentional trigger dep to re-run the effect on retry without being read inside the body
  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const currentParams = params;

    if (currentParams === null) {
      setCredentials(null);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    setCredentials(null);
    setIsLoading(true);
    setIsError(false);
    let cancelled = false;

    const initialize = async () => {
      try {
        const chatData = await teamChatJoin(currentParams);

        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        if (!(chatData.token && chatData.acsUserId && chatData.endpoint)) {
          throw new Error("Invalid chat credentials received");
        }

        setCredentials(chatData);
        setIsLoading(false);
      } catch (error) {
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        setCredentials(null);
        setIsLoading(false);
        setIsError(true);
        recordErrorInSentry(error);
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [params, retryCount]);

  const retry = useCallback(() => setRetryCount((c) => c + 1), []);

  const refreshToken = useCallback<RefreshTokenFn>(async () => {
    const currentParams = paramsRef.current;
    if (currentParams === null) {
      throw new Error("Cannot refresh token: missing params");
    }

    const chatData = await teamChatJoin(currentParams);

    setCredentials((currentCredentials) => {
      if (!currentCredentials) {
        return currentCredentials;
      }

      if (
        currentCredentials.token === chatData.token &&
        currentCredentials.endpoint === chatData.endpoint &&
        currentCredentials.acsUserId === chatData.acsUserId
      ) {
        return currentCredentials;
      }

      return {
        ...currentCredentials,
        acsUserId: chatData.acsUserId,
        endpoint: chatData.endpoint,
        token: chatData.token,
      };
    });

    return {
      expiresOn: new Date(Date.now() + TOKEN_EXPIRY_MS),
      token: chatData.token,
    };
  }, []);

  const canJoin = params !== null;

  return {
    credentials,
    isError,
    isLoading,
    refreshToken: canJoin ? refreshToken : null,
    retry,
    threadId: credentials?.acsThreadId ?? null,
  };
}
